import type {
  BillingGetInfoResponse,
  BillingInfo,
  BillingOrdwayCouponModel,
  BillingOrdwayCustomerModel,
  BillingOrdwayProfileResponse,
  BillingOrdwaySubscriptionModel,
  Coupon,
  Plan,
  Subscription,
  TaxRate,
  Tier,
} from '@customerio/hydra-ui-api';
import { atom, computed } from 'nanostores';
import { hydraUiApi } from '@/services/api';
import { AccountState } from '@/services/auth/types';
import { accountStore } from '@/services/auth/store';
import { enqueueTask } from '@/utils/enqueue-task';
import { isEmptyString } from '@/utils/string';

/** A single product line on an Ordway subscription (`subscription.plans[]`). */
export type OrdwaySubscriptionProduct = NonNullable<BillingOrdwaySubscriptionModel['plans']>[number];

/** A subscription is either the Stripe model or the Ordway model. */
export type BillingSubscription = Subscription | BillingOrdwaySubscriptionModel;

/** A coupon is either the Stripe model or the Ordway model. */
export type BillingCoupon = Coupon | BillingOrdwayCouponModel;

/** Options for {@link billing.initialize}. Mirrors the Ember billing service's `initialize` task. */
export interface InitializeBillingOptions {
  /** Re-fetch even when already initialized (subject to a short throttle). */
  refresh?: boolean;
  /** Swallow request errors and still mark the service initialized. */
  ignoreErrors?: boolean;
  /** Plan slug from a URL query param; selects {@link billing.paramPlan} from the returned plans. Bypasses the refresh throttle. */
  paramPlanSlug?: string;
  /** Whether to include coupon details in the Ordway profile response. */
  includeCoupons?: boolean;
}

/**
 * Refresh throttle (ms). Mirrors the Ember billing service: when already
 * initialized and refreshed without a coupon/plan param, suppress refreshes
 * that land within this window (the user is likely just being redirected).
 */
const RECENTLY_INITIALIZED_WINDOW_MS = 500;

/**
 * Sorts Ordway subscriptions ascending by service start date, oldest first.
 * Subscriptions without a start date sort last (matching lodash `sortBy`).
 */
function sortOrdwaySubscriptionsByDate(
  subscriptions: readonly BillingOrdwaySubscriptionModel[],
): BillingOrdwaySubscriptionModel[] {
  return [...subscriptions].sort((a, b) => {
    const aTime = isEmptyString(a.service_start_date) ? Infinity : Date.parse(a.service_start_date ?? '');
    const bTime = isEmptyString(b.service_start_date) ? Infinity : Date.parse(b.service_start_date ?? '');
    return aTime - bTime;
  });
}

/**
 * The backend does not filter future-subscription products the way it filters
 * the effective subscription's, so collapse duplicate `product_id`s here. When
 * a product has no charge start date it's aligned with the service start date
 * and wins; otherwise the earliest charge start date wins. Ports Ember
 * `#setOrdwayBillingProperties`.
 */
function dedupeFutureSubscriptionProducts(products: readonly OrdwaySubscriptionProduct[]): OrdwaySubscriptionProduct[] {
  const productById: Record<string, OrdwaySubscriptionProduct> = {};

  for (const product of products) {
    const existing = productById[product.product_id];

    if (existing == null) {
      productById[product.product_id] = product;
      continue;
    }

    if (isEmptyString(product.charge_start_date)) {
      // No charge start date means the product is aligned with the service
      // start date — prefer it.
      productById[product.product_id] = product;
    } else if (!isEmptyString(existing.charge_start_date)) {
      // Both have a charge start date: keep the earliest.
      if (Date.parse(product.charge_start_date ?? '') < Date.parse(existing.charge_start_date ?? '')) {
        productById[product.product_id] = product;
      }
    }
  }

  return Object.values(productById);
}

/**
 * Singleton billing service for the Hydra React app.
 *
 * Modelled as a plain object — a curated public API rather than a class or a
 * module that re-exports everything — following the "limiting public APIs"
 * pattern (https://zenn.dev/dk_/articles/bc779b8fba68b0?locale=en#limiting-public-apis).
 *
 * State is held in nanostore atoms so the UI can subscribe and re-render.
 * Account-derived flags are computeds over the shared `accountStore`, so they
 * track account changes without any explicit wiring. Exposed on
 * `window.__hydra__.billing` for cross-app (Ember) consumption.
 */
export const billing = {
  isOrdwayCustomer: computed(accountStore, (account) => account?.ordway_id != null && account.ordway_id.length > 0),
  isOrdwayCustomerWaitingInitialization: computed(
    accountStore,
    (account) => account?.ordway_id?.startsWith('TEMP-') === true,
  ),

  initialized: atom(false),
  initializedAt: null as number | null,

  subscription: atom<BillingSubscription | undefined>(undefined),
  coupon: atom<BillingCoupon | undefined>(undefined),
  taxRate: atom<TaxRate | undefined>(undefined),

  /** Plan matching `paramPlanSlug`, resolved from {@link billing.plans}. */
  paramPlan: atom<Plan | null>(null),

  /** Legacy stripe billing info. Undefined for Ordway accounts. */
  billingInfo: atom<BillingInfo | undefined>(undefined),
  plans: atom<Plan[] | undefined>(undefined),
  tiers: atom<Tier[] | undefined>(undefined),

  /** Ordway billing data. Undefined for legacy stripe and metronome accounts. */
  customer: atom<BillingOrdwayCustomerModel | undefined>(undefined),
  futureSubscription: atom<BillingOrdwaySubscriptionModel | undefined>(undefined),
  products: atom<OrdwaySubscriptionProduct[] | undefined>(undefined),

  setStripeBillingProperties(res: BillingGetInfoResponse, paramPlanSlug: string) {
    const plans = res.plans ?? [];

    this.billingInfo.set(res.billing_info ?? undefined);
    this.subscription.set(res.subscription ?? undefined);
    this.coupon.set(res.coupon ?? undefined);
    this.plans.set(plans);
    this.tiers.set(res.tiers ?? []);
    this.taxRate.set(res.tax_rate ?? undefined);
    this.paramPlan.set(
      isEmptyString(paramPlanSlug) ? null : (plans.find((plan) => plan.slug === paramPlanSlug) ?? null),
    );
    // reset ordway properties to clear stale data
    this.customer.set(undefined);
    this.futureSubscription.set(undefined);
    this.products.set(undefined);
  },

  // fallow-ignore-next-line complexity
  setOrdwayBillingProperties(res: BillingOrdwayProfileResponse) {
    const allSubscriptions = sortOrdwaySubscriptionsByDate(res.ordway_subscriptions ?? []);

    const subscription = allSubscriptions[0];
    let futureSubscription = allSubscriptions.length > 1 ? allSubscriptions[1] : undefined;

    if (futureSubscription != null) {
      futureSubscription = {
        ...futureSubscription,
        plans: dedupeFutureSubscriptionProducts(futureSubscription.plans ?? []),
      };
    }

    this.customer.set(res.ordway_customer ?? undefined);
    this.subscription.set(subscription ?? undefined);
    this.futureSubscription.set(futureSubscription);
    this.coupon.set(res.coupon ?? undefined);
    this.products.set(subscription?.plans ?? []);
    // reset legacy stripe properties to clear stale data
    this.billingInfo.set(undefined);
    this.plans.set(undefined);
    this.tiers.set(undefined);
  },

  /**
   * Loads billing data for the current account into the stores above.
   *
   * An enqueued task (à la ember-concurrency): call `billing.initialize.perform(...)`.
   * Concurrent callers are serialized so they share one in-flight load; `perform`
   * is a closure, so it stays callable when destructured or invoked off the window
   * global. The body references `billing` directly (not `this`) since it runs
   * outside the object's method context.
   */
  initialize: enqueueTask(
    // fallow-ignore-next-line complexity
    async ({
      refresh = false,
      ignoreErrors = false,
      paramPlanSlug = '',
      includeCoupons = false,
    }: InitializeBillingOptions = {}): Promise<void> => {
      if (billing.initialized.get() && !refresh) {
        return;
      }

      // If the service was recently initialized, return early even when refreshing,
      // since the user probably hasn't taken an action that warrants a refresh
      // (we're likely just redirecting). A plan param means the data would differ,
      // so don't short-circuit it.
      const initializedAt = billing.initializedAt ?? 0;
      const recentlyInitialized = Date.now() - initializedAt <= RECENTLY_INITIALIZED_WINDOW_MS;

      if (billing.initialized.get() && refresh && paramPlanSlug === '' && recentlyInitialized) {
        return;
      }

      const account = accountStore.get();
      if (account == null) {
        return;
      }

      const isOrdway = billing.isOrdwayCustomer.get();

      // The Ordway profile endpoint is expected to fail for suspended/canceled
      // accounts and for customers still waiting on Ordway provisioning, so widen
      // `ignoreErrors` for those cases (matching the Ember service).
      const ordwayIgnoreErrors =
        ignoreErrors ||
        account.account_state === AccountState.Suspended ||
        account.account_state === AccountState.Canceled ||
        billing.isOrdwayCustomerWaitingInitialization.get();

      const effectiveIgnoreErrors = isOrdway ? ordwayIgnoreErrors : ignoreErrors;

      try {
        if (isOrdway) {
          const res = await hydraUiApi.billing.getProfile({
            params: {
              path: { account_id: String(account.id) },
              query: { coupons: String(includeCoupons) },
            },
          });
          billing.setOrdwayBillingProperties(res);
        } else {
          const res = await hydraUiApi.billing.getInfo({
            params: { path: { account_id: String(account.id) } },
          });
          billing.setStripeBillingProperties(res, paramPlanSlug);
        }

        billing.initialized.set(true);
        billing.initializedAt = Date.now();
      } catch (error) {
        if (effectiveIgnoreErrors) {
          billing.initialized.set(true);
          billing.initializedAt = Date.now();
          return;
        }
        throw error;
      }
    },
  ),
};

/** Public API surface of the Hydra billing service. */
export type BillingService = typeof billing;
